import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { patchClientIntegrations } from '../api/integrations.api';
import { ClientIntegration } from '../types/client';

export interface PatchClientIntegrationsParams {
  clientId: string | number;
  integrations?: ClientIntegration[];
  onSuccess?: () => void;
}

/**
 * Hook to patch client integrations.
 * Invalidates client integrations queries on success and displays toast notifications.
 */
export const usePatchClientIntegrations = (): UseMutationResult<
  void,
  Error,
  PatchClientIntegrationsParams
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.clients']);
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: async ({ clientId, integrations }: PatchClientIntegrationsParams) => {
      return patchClientIntegrations(clientId, integrations);
    },
    onSuccess: async (_, variables) => {
      // First, invalidate and refetch the client data to get fresh rawIntegrations
      if (variables.clientId) {
        await queryClient.invalidateQueries({
          queryKey: ['clients', variables.clientId.toString()],
        });
        // Wait for the client query to refetch
        await queryClient.refetchQueries({
          queryKey: ['clients', variables.clientId.toString()],
        });
      }

      // Then invalidate integrations queries (they depend on rawIntegrations from client)
      await queryClient.invalidateQueries({
        queryKey: ['available-client-integrations'],
      });

      // Display success toast
      displayToastSuccess(t('authenticated.clients:toasts.clientUpdatedSuccess'));

      // Call success callback if provided
      if (variables.onSuccess) {
        variables.onSuccess();
      }
    },
    onError: error => {
      // Display error toast
      displayToastErrorFromApiWithFallback(error, 'authenticated.clients:toasts.updateClientError');
    },
  });
};
